/**
 * Send Feedback — the reply half (v3.90.0).
 *
 * An administrator answers a message on Admin Dashboard → Feedback; the
 * sender sees the answer in the app. Everything lives on the message's own
 * `app_feedback` doc — no new collection:
 *
 *   replyText    string     the administrator's reply (≤ REPLY_MAX_LENGTH)
 *   replyAt      timestamp  when it was written (server time)
 *   replyByName  string     who wrote it, as shown to the sender
 *   replyUnread  boolean    true until the sender acknowledges it
 *   replySeenAt  timestamp  when they did (server time)
 *
 * Rules (firestore.rules, match /app_feedback): the administrator writes the
 * reply fields; the SENDER may read their own messages and flip replyUnread
 * true → false with replySeenAt = the server time, nothing else. A sender's
 * read is denied unless the query carries `userId == <their uid>` — both
 * queries below do.
 *
 * Read budget (docs/perf/PERFORMANCE.md): fetchUnreadReplies is ONE bounded
 * query per signed-in page load (two equality filters — index merge, no
 * composite index), run once more only when a "Got it" write is refused;
 * fetchMyFeedback runs only when the sender opens "Your messages". Neither is
 * a listener.
 */
import {
  collection, deleteField, doc, getDocs, limit, query, serverTimestamp, updateDoc, where,
} from 'firebase/firestore';
import { db } from '../firebase';

export const REPLY_MAX_LENGTH = 2000;
/** The create rule's limit on a message (firestore.rules → app_feedback). */
export const MESSAGE_MAX_LENGTH = 5000;
export const UNREAD_REPLY_LIMIT = 5;
export const MY_FEEDBACK_LIMIT = 50;

const millis = (ts) => (ts && typeof ts.toMillis === 'function' ? ts.toMillis() : 0);

/**
 * A stored value as display text. The create rule type-checks a new
 * message (v3.90.0), but docs written before it, by the Admin SDK or by hand
 * in the console are not bound by it — so nothing read from `app_feedback`
 * is rendered without passing through here: an object in a text slot would
 * crash the page that shows it.
 */
export const asText = (value, fallback = '') => (typeof value === 'string' && value.trim() ? value : fallback);

// The form's three types (the create rule accepts exactly these). Maps, not
// object literals: a stored `type` of '__proto__' or 'constructor' must find
// nothing, not Object.prototype.
const TYPE_LABELS = new Map([['Question', 'Question'], ['Bug', 'Bug'], ['Feature Request', 'Feature request']]);
const TYPE_COLORS = new Map([
  ['Bug', { background: '#fee2e2', color: '#b91c1c' }],
  ['Feature Request', { background: '#dcfce7', color: '#15803d' }],
]);
const DEFAULT_TYPE_COLORS = { background: '#e0f2fe', color: '#0369a1' };

/** "Feature request" for a known type; the stored text for an older one; "Message" for anything else. */
export const feedbackTypeLabel = (type) => (typeof type === 'string' && TYPE_LABELS.get(type)) || asText(type, 'Message');
/** { background, color } for the type chip — always a fresh plain object. */
export const feedbackTypeColors = (type) => ({ ...((typeof type === 'string' && TYPE_COLORS.get(type)) || DEFAULT_TYPE_COLORS) });

export const hasReply = (item) => typeof item?.replyText === 'string' && item.replyText.trim().length > 0;
export const isReplyUnread = (item) => hasReply(item) && item.replyUnread === true;

/** "Oct 3, 2026" — or '' when the timestamp is missing (a pending server write). */
export function formatFeedbackDate(ts) {
  if (!ts || typeof ts.toDate !== 'function') return '';
  const date = ts.toDate();
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** "10/3/2026, 3:05:00 PM" for a table cell — or the fallback when the value is not a timestamp. */
export function formatFeedbackDateTime(ts, fallback = 'N/A') {
  if (!ts || typeof ts.toDate !== 'function') return fallback;
  const date = ts.toDate();
  return date instanceof Date && !Number.isNaN(date.getTime()) ? date.toLocaleString() : fallback;
}

/** One line of a longer message, for a card heading. Never cuts inside a word when it can avoid it. */
export function excerpt(text, max = 160) {
  const flat = asText(text).replace(/\s+/g, ' ').trim();
  if (flat.length <= max) return flat;
  const cut = flat.slice(0, max);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}

/** The sender's messages that carry a reply they have not acknowledged — newest reply first. */
export async function fetchUnreadReplies(uid) {
  if (!uid) return [];
  const snap = await getDocs(query(
    collection(db, 'app_feedback'),
    where('userId', '==', uid),
    where('replyUnread', '==', true),
    limit(UNREAD_REPLY_LIMIT)
  ));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter(isReplyUnread)
    .sort((a, b) => millis(b.replyAt) - millis(a.replyAt));
}

/** Everything the sender has sent, newest first, replies included. Loaded on demand. */
export async function fetchMyFeedback(uid) {
  if (!uid) return [];
  // No orderBy: where(userId) + orderBy(timestamp) would need a composite
  // index. A sender has a handful of messages; sort them here.
  const snap = await getDocs(query(
    collection(db, 'app_feedback'),
    where('userId', '==', uid),
    limit(MY_FEEDBACK_LIMIT)
  ));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => millis(b.timestamp) - millis(a.timestamp));
}

/** The sender's acknowledgement — the only write the rules allow them. */
export async function markReplySeen(feedbackId) {
  await updateDoc(doc(db, 'app_feedback', feedbackId), {
    replyUnread: false,
    replySeenAt: serverTimestamp(),
  });
}

/**
 * The administrator's reply. Writing it again replaces the earlier text and
 * makes it unread again, so the sender is shown the new one.
 */
export async function sendFeedbackReply(feedbackId, text, byName) {
  const replyText = String(text || '').trim();
  if (!replyText) throw new Error('A reply needs some text.');
  if (replyText.length > REPLY_MAX_LENGTH) throw new Error(`A reply can be at most ${REPLY_MAX_LENGTH} characters.`);
  await updateDoc(doc(db, 'app_feedback', feedbackId), {
    replyText,
    replyAt: serverTimestamp(),
    replyByName: String(byName || '').trim().slice(0, 120) || 'Literacy Assessments',
    replyUnread: true,
    replySeenAt: deleteField(),
  });
}
